import { httpGet, httpPost } from "@/api/connection";
import { Contact } from "./contacts.types";

export type GetContactsQuery = {
  contact_id?: number;
  case_id?: number;
  /**
   * Adds an `assigned_to_case` flag to every record, saying whether that
   * contact is already on this case. Unlike `case_id` — which INNER JOINs
   * contact_assign and narrows the result set *to* the case's contacts — this
   * leaves the set alone, which is what a picker needs.
   */
  assigned_case_id?: number;
  search?: string;
  page?: number;
  pageSize?: number;
  page_size?: number;
};

export type GetContactsResponse = {
  data: Contact[];
  total: number;
  pageCount: number;
  page_count: number;
  currentPage: number;
  page: number;
  nextPage: number | null;
  next_page: number | null;
  perPage: number;
};

export type GetContactsFn = (
  options?: GetContactsQuery
) => Promise<GetContactsResponse>;

const getContacts: GetContactsFn = async (options) => {
  const { contact_id } = options || {};
  if (contact_id) {
    return httpGet(`/contacts/${contact_id}`);
  }
  return httpGet("/contacts", options);
};

export type LinkCaseContactBody = {
  /**
   * False when the contact was already on the case, so INSERT IGNORE dropped
   * the row and nothing changed. Absent on an API predating the flag — callers
   * must not read a missing value as "already assigned".
   */
  linked?: boolean;
  /** The API layer's failure shape (connection.js handleError). */
  success?: false;
  error?: unknown;
};

/**
 * An API predating the `linked` flag answers a successful link with 200 and no
 * body — its service returns nothing for the route to serialise — and axios
 * hands a bodyless response back as `""`. In the type because it is the shape
 * that broke this call once already (ENG-1688): a link is not an object, and
 * anything reading the response has to say what it does with an empty one.
 */
export type LinkCaseContactResponse = LinkCaseContactBody | "";

async function linkCaseContact(
  values: {
    case_id: number;
    client_id: number;
  },
  options: {
    /**
     * Opts the request out of the axios interceptor's error toast. The
     * interceptor toasts every failed request before handleError resolves it
     * as { success: false } (connection.js), so a caller that reports failures
     * itself — one summary for a whole batch — otherwise gets a toast per
     * request on top of its own message.
     */
    skipErrorToast?: boolean;
  } = {}
): Promise<LinkCaseContactResponse> {
  return await httpPost(`/contacts/${values.client_id}/link`, values, options);
}

export const ContactsAPI = {
  get: getContacts,
  linkCaseContact,
};
